import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import { categoriesAPI, mediaAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Layers, Plus, Edit, Trash2, Save, X, Image as ImageIcon, Upload } from 'lucide-react';
import MediaSelectorModal from '../../components/admin/MediaSelectorModal';
import { resolveImageUrl } from '../../utils/imageUrl';

const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', slug: '', description: '', icon: '', imageUrl: '', active: true, displayOrder: 0 });
  const [saving, setSaving] = useState(false);
  const [showMedia, setShowMedia] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { loadCategories(); }, []);

  const loadCategories = async () => {
    try {
      const data = await categoriesAPI.list();
      setCategories(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const resetForm = () => {
    setForm({ name: '', slug: '', description: '', icon: '', imageUrl: '', active: true, displayOrder: 0 });
    setEditing(null);
    setShowForm(false);
  };

  const generateSlug = (name) =>
    name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const handleEdit = (cat) => {
    setForm({
      name: cat.name, slug: cat.slug, description: cat.description || '',
      icon: cat.icon || '', imageUrl: cat.imageUrl || '', active: cat.active !== false, displayOrder: cat.displayOrder || 0
    });
    setEditing(cat.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.slug || !form.description) { alert('Preencha Nome, Slug e Descrição'); return; }
    setSaving(true);
    try {
      if (editing) { await categoriesAPI.update(editing, form); }
      else { await categoriesAPI.create(form); }
      resetForm();
      loadCategories();
    } catch (error) { alert('Erro: ' + (error.response?.data?.detail || error.message)); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Excluir categoria "${name}"?`)) return;
    try {
      await categoriesAPI.delete(id);
      setCategories(categories.filter(c => c.id !== id));
    } catch (error) { alert('Erro: ' + (error.response?.data?.detail || error.message)); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await mediaAPI.upload(file, form.name || 'categoria', '', 'categorias');
      const url = resolveImageUrl(result.url || result.filename);
      setForm({ ...form, imageUrl: url });
    } catch (error) { alert('Erro ao enviar imagem'); }
    finally { setUploading(false); }
  };

  return (
    <AdminLayout>
      <div>
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 data-testid="categories-page-title" className="text-2xl font-bold text-gray-800">Categorias</h1>
            <p className="text-gray-600">{categories.length} categorias</p>
          </div>
          <Button data-testid="add-category-btn" onClick={() => { resetForm(); setShowForm(true); }} className="bg-[#055509] hover:bg-[#0a7a0f] text-white">
            <Plus size={18} className="mr-2" /> Nova Categoria
          </Button>
        </div>

        {showForm && (
          <Card className="mb-6 border-[#055509]">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>{editing ? 'Editar Categoria' : 'Nova Categoria'}</CardTitle>
                <Button variant="ghost" size="sm" onClick={resetForm}><X size={18} /></Button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                    <Input data-testid="category-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value, slug: editing ? form.slug : generateSlug(e.target.value) })} placeholder="Nome da categoria" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Slug *</label>
                    <Input data-testid="category-slug-input" value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} required />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descrição *</label>
                  <textarea data-testid="category-desc-input" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="w-full border rounded-md px-3 py-2 text-sm min-h-[80px]" required />
                </div>

                {/* Cover Image Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Foto de Capa</label>
                  <div className="flex flex-col gap-3">
                    {form.imageUrl && (
                      <div className="relative w-48 h-32 rounded-lg overflow-hidden border group">
                        <img src={resolveImageUrl(form.imageUrl)} alt="Capa" className="w-full h-full object-cover" />
                        <button type="button" onClick={() => setForm({ ...form, imageUrl: '' })} className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <X size={12} />
                        </button>
                      </div>
                    )}
                    <div className="flex gap-2 flex-wrap">
                      <label className="cursor-pointer">
                        <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                        <div data-testid="category-upload-btn" className="flex items-center gap-2 px-4 py-2 border rounded-md text-sm hover:bg-gray-50 transition-colors">
                          <Upload size={16} />
                          {uploading ? 'Enviando...' : 'Upload do Computador'}
                        </div>
                      </label>
                      <Button type="button" variant="outline" size="sm" data-testid="category-media-btn" onClick={() => setShowMedia(true)}>
                        <ImageIcon size={16} className="mr-2" /> Biblioteca de Mídia
                      </Button>
                    </div>
                    <div>
                      <Input value={form.imageUrl} onChange={e => setForm({ ...form, imageUrl: e.target.value })} placeholder="Ou cole a URL da imagem aqui" className="text-xs" />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ícone (nome Lucide)</label>
                    <Input value={form.icon} onChange={e => setForm({ ...form, icon: e.target.value })} placeholder="Ex: Droplets" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ordem</label>
                    <Input type="number" value={form.displayOrder} onChange={e => setForm({ ...form, displayOrder: parseInt(e.target.value) || 0 })} />
                  </div>
                </div>
                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={resetForm}>Cancelar</Button>
                  <Button data-testid="save-category-btn" type="submit" className="bg-[#055509] hover:bg-[#0a7a0f] text-white" disabled={saving}>
                    {saving ? 'Salvando...' : <><Save size={16} className="mr-2" />{editing ? 'Atualizar' : 'Criar'}</>}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* List */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#055509]" /></div>
            ) : categories.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Layers size={48} className="mx-auto mb-4 opacity-50" />
                <p>Nenhuma categoria cadastrada</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Imagem</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Nome</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600 hidden md:table-cell">Slug</th>
                      <th className="text-center px-4 py-3 text-sm font-semibold text-gray-600">Produtos</th>
                      <th className="text-center px-4 py-3 text-sm font-semibold text-gray-600">Status</th>
                      <th className="text-right px-4 py-3 text-sm font-semibold text-gray-600">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {categories.map(cat => (
                      <tr key={cat.id} className="hover:bg-gray-50" data-testid={`category-row-${cat.id}`}>
                        <td className="px-4 py-3">
                          {cat.imageUrl ? (
                            <img src={resolveImageUrl(cat.imageUrl)} alt={cat.name} className="w-12 h-12 rounded object-cover" />
                          ) : (
                            <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center">
                              <ImageIcon size={16} className="text-gray-400" />
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-800">{cat.name}</div>
                          <div className="text-xs text-gray-500">{cat.description?.substring(0, 50)}...</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 hidden md:table-cell">{cat.slug}</td>
                        <td className="px-4 py-3 text-center text-sm">{cat.productCount || 0}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 text-xs font-semibold rounded ${cat.active !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                            {cat.active !== false ? 'Ativa' : 'Inativa'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button data-testid={`edit-category-${cat.id}`} variant="ghost" size="sm" onClick={() => handleEdit(cat)}><Edit size={16} /></Button>
                            <Button data-testid={`delete-category-${cat.id}`} variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(cat.id, cat.name)}><Trash2 size={16} /></Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <MediaSelectorModal
        isOpen={showMedia}
        onClose={() => setShowMedia(false)}
        onSelect={(url) => setForm({ ...form, imageUrl: url })}
      />
    </AdminLayout>
  );
};

export default AdminCategories;
